/**
 * Relative timestamps rendered by the browser.
 *
 * The wording comes from the texts the page carries, so a card appended to
 * the stream is dated in the language picked in the header. The Slovak
 * wording stays here as the fallback for a page without the island.
 */

import { text } from "./i18n";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;
const WEEK_MS = 7 * DAY_MS;
const MONTH_MS = 30 * DAY_MS;
const YEAR_MS = 365 * DAY_MS;

/** Return the wording of `key` with the counter inside it. */
function ago(key: string, fallback: string, count: number): string {
    return text(`js.ago.${key}`, fallback, { count });
}

/**
 * Return a relative description of `value`, for example "pred 3 dňami".
 *
 * An empty or unparsable timestamp yields an empty string so that the caller
 * can simply leave the element blank.
 */
export function relativeDate(value: string | null, now: Date = new Date()): string {
    if (!value) {
        return "";
    }

    const stamp = Date.parse(value);
    if (Number.isNaN(stamp)) {
        return "";
    }

    const diff = now.getTime() - stamp;

    if (diff < MINUTE_MS) {
        return text("js.ago.moments", "pred chvíľou");
    }

    if (diff < HOUR_MS) {
        return ago("minutes", "pred {count} min.", Math.floor(diff / MINUTE_MS));
    }

    if (diff < DAY_MS) {
        return ago("hours", "pred {count} h", Math.floor(diff / HOUR_MS));
    }

    if (diff < WEEK_MS) {
        return ago("days", "pred {count} d", Math.floor(diff / DAY_MS));
    }

    if (diff < MONTH_MS) {
        return ago("weeks", "pred {count} týž.", Math.floor(diff / WEEK_MS));
    }

    if (diff < YEAR_MS) {
        return ago("months", "pred {count} mes.", Math.floor(diff / MONTH_MS));
    }

    return ago("years", "pred {count} r.", Math.floor(diff / YEAR_MS));
}
